import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { useTranslation } from "react-i18next";
import Layout from "@/components/layout/Layout";
import LoginPage from "@/pages/LoginPage";
import PublicHome from "@/pages/public/PublicHome";
import DashboardPage from "@/pages/DashboardPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import EstimatesPage from "@/pages/EstimatesPage";
import JobsPage from "@/pages/JobsPage";import JobDetailPage from "@/pages/JobDetailPage";
import ContactsPage from "@/pages/ContactsPage";
import ContactDetailPage from "@/pages/ContactDetailPage";
import NotificationsPage from "@/pages/NotificationsPage";
import CalendarPage from "@/pages/CalendarPage";
import PermitsPage from "@/pages/PermitsPage";
import EquipmentPage from "@/pages/EquipmentPage";
import MapsPage from "@/pages/MapsPage";
import InvoicesPage from "@/pages/InvoicesPage";
import TimecardsPage from "@/pages/TimecardsPage";
import InventoryPage from "@/pages/InventoryPage";
import ExpensesPage from "@/pages/ExpensesPage";
import LineItemsPage from "@/pages/LineItemsPage";
import EstimateTemplatesPage from "@/pages/EstimateTemplatesPage";
import ReportsPage from "@/pages/ReportsPage";
import StatsPage from "@/pages/StatsPage";
import CommissionsPage from "@/pages/CommissionsPage";
import PublicBookingPage from "@/pages/PublicBookingPage";
import PublicEstimatePage from "@/pages/PublicEstimatePage";
import PublicInvoicePage from "@/pages/PublicInvoicePage";
import PublicFormSignPage from "@/pages/PublicFormSignPage";
import BookingsPage from "@/pages/BookingsPage";
import SettingsPage from "@/pages/SettingsPage";
import HelpPage from "@/pages/HelpPage";
import PumpServicePage from "@/pages/PumpServicePage";
import JobTemplatesPage from "@/pages/JobTemplatesPage";
import CustomFormsPage from "@/pages/CustomFormsPage";
import PermitsRegistryPage from "@/pages/PermitsRegistryPage";
import InvoiceTemplatesPage from "@/pages/InvoiceTemplatesPage";
import ElectronicPaymentsPage from "@/pages/ElectronicPaymentsPage";
import CallHistoryPage from "@/pages/CallHistoryPage";
import PhonePage from "@/pages/PhonePage";
import ContractorPortal from "@/pages/ContractorPortal";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-3 border-[#003366]/20 border-t-[#003366] rounded-full animate-spin" />
          <p className="text-sm text-gray-400 font-medium">{t('common.loading')}</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Contractors only get the limited portal — keep them out of the full CRM.
function ContractorGate({ children }) {
  const { user } = useAuth();
  if (user?.role === "contractor") return <Navigate to="/portal" replace />;
  return children;
}

// Blocks access (and redirects) to a module's routes when the logged-in
// role's permissions matrix has `view: false` for that module — e.g. a
// driller typing /estimates directly in the URL bar should NOT reach the
// page just because it's hidden from the sidebar.
function ModuleRoute({ module, children }) {
  const { hasPermission } = useAuth();
  if (!hasPermission(module, "view")) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="w-10 h-10 border-3 border-[#003366]/20 border-t-[#003366] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<PublicHome />} />
      <Route path="/login" element={user ? <Navigate to={user.role === 'contractor' ? '/portal' : '/dashboard'} replace /> : <LoginPage />} />
      <Route path="/booking" element={<PublicBookingPage />} />
      <Route path="/estimate/:token" element={<PublicEstimatePage />} />
      <Route path="/pay/:token" element={<PublicInvoicePage />} />
      <Route path="/sign/:token" element={<PublicFormSignPage />} />
      <Route path="/portal" element={<ProtectedRoute><ContractorPortal /></ProtectedRoute>} />
      <Route
        element={
          <ProtectedRoute>
            <ContractorGate>
              <Layout />
            </ContractorGate>
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/projects" element={<ModuleRoute module="projects"><ProjectsPage /></ModuleRoute>} />
        <Route path="/projects/:id" element={<ModuleRoute module="projects"><ProjectDetailPage /></ModuleRoute>} />
        <Route path="/estimates" element={<ModuleRoute module="estimates"><EstimatesPage /></ModuleRoute>} />
        <Route path="/estimate-templates" element={<ModuleRoute module="estimates"><EstimateTemplatesPage /></ModuleRoute>} />
        <Route path="/jobs" element={<JobsPage />} />
        <Route path="/jobs/:id" element={<JobDetailPage />} />
        <Route path="/job-templates" element={<JobTemplatesPage />} />
        <Route path="/contacts" element={<ModuleRoute module="contacts"><ContactsPage /></ModuleRoute>} />
        <Route path="/contacts/:id" element={<ModuleRoute module="contacts"><ContactDetailPage /></ModuleRoute>} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/permits" element={<ModuleRoute module="permits"><PermitsPage /></ModuleRoute>} />
        <Route path="/equipment" element={<EquipmentPage />} />
        <Route path="/maps" element={<MapsPage />} />
        <Route path="/invoices" element={<ModuleRoute module="invoices"><InvoicesPage /></ModuleRoute>} />
        <Route path="/invoice-templates" element={<ModuleRoute module="invoices"><InvoiceTemplatesPage /></ModuleRoute>} />
        <Route path="/electronic-payments" element={<ModuleRoute module="invoices"><ElectronicPaymentsPage /></ModuleRoute>} />
        <Route path="/commissions" element={<CommissionsPage />} />
        <Route path="/timecards" element={<TimecardsPage />} />
        <Route path="/inventory" element={<ModuleRoute module="inventory"><InventoryPage /></ModuleRoute>} />
        <Route path="/expenses" element={<ExpensesPage />} />
        <Route path="/line-items" element={<LineItemsPage />} />
        <Route path="/reports" element={<ModuleRoute module="reports"><ReportsPage /></ModuleRoute>} />
        <Route path="/stats" element={<ModuleRoute module="reports"><StatsPage /></ModuleRoute>} />
        <Route path="/bookings" element={<BookingsPage />} />
        <Route path="/settings" element={<ModuleRoute module="settings"><SettingsPage /></ModuleRoute>} />
        <Route path="/custom-forms" element={<CustomFormsPage />} />
        <Route path="/permits-registry" element={<ModuleRoute module="permits"><PermitsRegistryPage /></ModuleRoute>} />
        <Route path="/pump-service" element={<PumpServicePage />} />
        <Route path="/pumps" element={<Navigate to="/pump-service" replace />} />
        <Route path="/calls" element={<Navigate to="/phone" replace />} />
        <Route path="/phone" element={<PhonePage />} />
        <Route path="/help" element={<HelpPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
